<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Centered modal wrapper used by every property-access modal.
 *
 *  • Teleports to <body>.
 *  • Backdrop blur + click-to-dismiss.
 *  • Escape-key dismiss.
 *  • Focus trap inside the card; restores focus on close.
 *  • `accent` prop drives the gold/green left-border + glow.
 */

const { t } = useI18n();

const {
  accent = 'gold',
  dismissable = true,
  labelledBy,
} = defineProps<{
  accent?: 'gold' | 'green';
  dismissable?: boolean;
  /** id of the title element inside the modal — for aria-labelledby. */
  labelledBy?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const cardRef = ref<HTMLElement | null>(null);
let previouslyFocused: HTMLElement | null = null;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const focusFirst = () => {
  const root = cardRef.value;
  if (!root) return;
  const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  (first ?? root).focus();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && dismissable) {
    event.preventDefault();
    emit('close');
    return;
  }
  if (event.key !== 'Tab' || !cardRef.value) return;

  const focusable = Array.from(cardRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled'),
  );
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

const handleBackdropClick = () => {
  if (dismissable) emit('close');
};

onMounted(() => {
  previouslyFocused = (document.activeElement as HTMLElement | null) ?? null;
  document.addEventListener('keydown', handleKeydown);
  // Lock body scroll while the modal is open.
  document.body.style.overflow = 'hidden';
  nextTick(focusFirst);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
  previouslyFocused?.focus?.();
});

const accentClasses = computed(() =>
  accent === 'green'
    ? 'border-t-success shadow-[0_24px_60px_-20px_rgba(34,197,94,0.35)]'
    : 'border-t-gold-primary-100 shadow-[0_24px_60px_-20px_rgba(255,182,72,0.35)]',
);
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[1000] flex items-center justify-center p-4 backdrop-blur-md bg-black/30"
      role="presentation"
      @click.self="handleBackdropClick"
    >
      <div
        ref="cardRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="labelledBy"
        tabindex="-1"
        class="relative w-full max-w-[460px] max-h-[92vh] overflow-y-auto rounded-2xl bg-white border-t-[3px] outline-none focus-visible:ring-2 focus-visible:ring-black-100/30"
        :class="accentClasses"
      >
        <button
          v-if="dismissable"
          type="button"
          :aria-label="t('common.actions.close')"
          class="absolute end-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-black-60 transition-colors hover:bg-black-05 hover:text-black-100"
          @click="emit('close')"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
            />
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
            />
          </svg>
        </button>
        <div class="px-7 pt-8 pb-7">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>
